import React, { useState, useEffect } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { MailPlus, RefreshCw, CheckCircle, ShieldCheck, Plug } from 'lucide-react';

/**
 * Configurador del canal de Email de atención al cliente (IMAP entrada + SMTP
 * salida de un buzón genérico, ej. soporte@tutienda.com). Sondeo cada 5 min.
 * Seguridad aplicada automáticamente: verificación de remitente (SPF/DKIM/DMARC),
 * allowlist de adjuntos por contenido real, y saneado de HTML — sin configuración.
 */
export const SupportEmailConfigSection = () => {
  const { organization } = useOrganization();
  const [config, setConfig] = useState({
    enabled: false,
    imap_host: '', imap_port: 993, imap_user: '', imap_password: '', imap_password_set: false,
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '', smtp_password_set: false,
    from_name: '', from_email: '',
    signature: '',
    ai_classification_enabled: true,
    last_poll_at: null, last_poll_error: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!organization?.id) return;
    api.get('/api/support-email/config')
      .then(r => setConfig(prev => ({ ...prev, ...r.data.config })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [organization?.id]);

  const upd = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/support-email/config', config);
      toast.success('Configuración de email de soporte guardada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      await api.post('/api/support-email/config/test-connection', {});
      toast.success('Conexión IMAP y SMTP correctas');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join(' · ') : (detail || 'Error de conexión');
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 flex items-center justify-center">
        <RefreshCw size={18} className="animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-6" data-testid="support-email-config-section">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
          <MailPlus size={18} className="text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold">Email de atención al cliente</h3>
          <p className="text-gray-400 text-sm mt-0.5">
            Conecta tu buzón de soporte (ej. soporte@tutienda.com) por IMAP/SMTP. Sera sondea cada 5 minutos,
            agrupa los emails en hilos y los vincula al mismo "Cliente" unificado que Chat/WhatsApp/Pedidos.
          </p>
        </div>
        <Switch data-testid="support-email-enabled-toggle" checked={config.enabled} onCheckedChange={v => upd('enabled', v)} />
      </div>

      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-start gap-2.5">
        <ShieldCheck size={15} className="text-emerald-400 flex-shrink-0 mt-0.5" />
        <p className="text-emerald-200/80 text-xs leading-relaxed">
          Protección automática incluida (sin configuración): verificación de remitente (SPF/DKIM/DMARC — remitentes
          suplantados se marcan pero nunca se descartan), adjuntos filtrados por contenido real (ejecutables/scripts
          bloqueados, imágenes reprocesadas), y HTML saneado antes de mostrarse.
        </p>
      </div>

      <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5">
        <h4 className="text-white font-medium text-sm mb-3">Entrada — IMAP</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Servidor IMAP</label>
            <input value={config.imap_host} onChange={e => upd('imap_host', e.target.value)} placeholder="imap.tudominio.com"
              data-testid="imap-host-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Puerto</label>
            <input type="number" value={config.imap_port} onChange={e => upd('imap_port', parseInt(e.target.value) || 993)}
              data-testid="imap-port-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Usuario (email)</label>
            <input value={config.imap_user} onChange={e => upd('imap_user', e.target.value)} placeholder="soporte@tudominio.com"
              data-testid="imap-user-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Contraseña {config.imap_password_set && <span className="text-green-400">(configurada)</span>}
            </label>
            <input type="password" value={config.imap_password} onChange={e => upd('imap_password', e.target.value)}
              placeholder={config.imap_password_set ? '••••••••' : 'Contraseña'}
              data-testid="imap-password-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
        </div>
      </div>

      <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5">
        <h4 className="text-white font-medium text-sm mb-3">Salida — SMTP (para responder)</h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Servidor SMTP</label>
            <input value={config.smtp_host} onChange={e => upd('smtp_host', e.target.value)} placeholder="smtp.tudominio.com"
              data-testid="support-smtp-host-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Puerto</label>
            <input type="number" value={config.smtp_port} onChange={e => upd('smtp_port', parseInt(e.target.value) || 587)}
              data-testid="support-smtp-port-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-500" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Usuario SMTP</label>
            <input value={config.smtp_user} onChange={e => upd('smtp_user', e.target.value)} placeholder="soporte@tudominio.com"
              data-testid="support-smtp-user-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">
              Contraseña SMTP {config.smtp_password_set && <span className="text-green-400">(configurada)</span>}
            </label>
            <input type="password" value={config.smtp_password} onChange={e => upd('smtp_password', e.target.value)}
              placeholder={config.smtp_password_set ? '••••••••' : 'Contraseña'}
              data-testid="support-smtp-password-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Nombre del remitente</label>
            <input value={config.from_name} onChange={e => upd('from_name', e.target.value)} placeholder="Soporte Mi Tienda"
              data-testid="support-from-name-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Email del remitente</label>
            <input value={config.from_email} onChange={e => upd('from_email', e.target.value)} placeholder="soporte@tudominio.com"
              data-testid="support-from-email-input" className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-gray-400 text-xs block mb-1">Firma / pie legal del email</label>
          <textarea value={config.signature} onChange={e => upd('signature', e.target.value)} rows={4}
            placeholder={"Ej:\nEl equipo de Soporte de Mi Tienda\nEste email y sus adjuntos son confidenciales..."}
            data-testid="support-email-signature-textarea"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 resize-none" />
          <p className="text-gray-600 text-xs mt-1">Se añade automáticamente al final de cada respuesta y cada email nuevo enviado desde este buzón.</p>
        </div>
      </div>

      <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-5 flex items-center justify-between">
        <div>
          <h4 className="text-white font-medium text-sm">Clasificación con IA</h4>
          <p className="text-gray-500 text-xs mt-1 max-w-md">
            La IA clasifica cada email y prepara un borrador de respuesta para los rutinarios — SIEMPRE
            queda pendiente de que un agente lo revise y lo envíe manualmente. Nunca se envía sola a clientes.
          </p>
        </div>
        <Switch data-testid="ai-classification-toggle" checked={config.ai_classification_enabled} onCheckedChange={v => upd('ai_classification_enabled', v)} />
      </div>

      {config.last_poll_at && (
        <p className="text-gray-500 text-xs">
          Último sondeo: {new Date(config.last_poll_at).toLocaleString('es-ES')}
          {config.last_poll_error && <span className="text-red-400 ml-2">— Error: {config.last_poll_error}</span>}
        </p>
      )}

      <div className="flex gap-3">
        <Button onClick={testConnection} disabled={testing} data-testid="test-support-email-connection-btn"
          variant="outline" className="flex-1 border-gray-700 text-gray-200 hover:bg-gray-800 gap-2">
          {testing ? <RefreshCw size={14} className="animate-spin" /> : <Plug size={14} />}
          {testing ? 'Probando...' : 'Probar conexión'}
        </Button>
        <Button onClick={save} disabled={saving} data-testid="save-support-email-config-btn"
          className="flex-1 bg-amber-600 hover:bg-amber-700 text-white gap-2">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </div>
    </div>
  );
};
